import { z } from 'zod';
import type { FrontendBuild } from '../types/frontend-build.type';
import { FRONTEND_UPDATE_TIMEOUT_MS } from '../constants/frontend-update.constants';

const frontendBuildSchema = z.object({
  id: z.string().trim().min(1).max(128),
  builtAt: z.iso.datetime({ offset: true }),
});

/**
 * Reads and validates the public deployment identity without using the browser cache.
 * @param signal - Query lifecycle cancellation signal.
 * @returns A validated build identity.
 * @throws Error on HTTP, content, validation, timeout, or network failure; the query handles it silently.
 */
export const fetchFrontendBuild = async (
  signal: AbortSignal
): Promise<FrontendBuild> => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  const timeout = setTimeout(abort, FRONTEND_UPDATE_TIMEOUT_MS);
  try {
    const response = await fetch('/version.json', {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (
      !response.ok ||
      !response.headers.get('content-type')?.includes('application/json')
    ) {
      throw new Error('Deployment manifest is unavailable');
    }
    const value: unknown = await response.json();
    return frontendBuildSchema.parse(value);
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', abort);
  }
};
